// react
import { useState } from 'react';

// material ui
import { Box } from '@mui/material';
import { DataGridPremium } from '@mui/x-data-grid-premium';

// shared components
import { DataGridToolbar } from '@/components/ui/datagrid/DataGridToolbar.tsx';

// components
import useSearchParams from '@/hooks/useStableSearchParams';

/**
 * DashboardDatagrid
 * - A thin wrapper around MUI DataGridPremium that applies dashboard-friendly styles (sx)
 * - Focus is on styling only; cell renderers and rows are passed through via props
 */
const DashboardDatagrid = ({
  disableColumnMenu = true,
  rows = [],
  label,
  isLoading,
  ...props
}) => {
  const [searchParams, setSearchParams] = useSearchParams();

  // Only leaf row IDs — used to filter out auto-generated group row IDs
  const rowIds = new Set(rows.map((r) => r.id));
  const unitIds = searchParams.get('units');

  const buildModelFromUrl = (currentUnitIds, currentRows) => {
    if (currentUnitIds === 'none') return { type: 'include', ids: new Set() };
    if (currentUnitIds) {
      return {
        type: 'include',
        ids: new Set(currentUnitIds.split(',').map((id) => Number(id) || id)),
      };
    }
    // Default: all selected
    return { type: 'include', ids: new Set(currentRows.map((r) => r.id)) };
  };

  // Store the FULL model as DataGrid emits it (leaf IDs + auto-generated group row IDs).
  // Passing the same model back prevents DataGrid from re-firing onRowSelectionModelChange
  // due to parents:true propagation computing group states that differ from a leaf-only model.
  const [selectionModel, setSelectionModel] = useState(() =>
    buildModelFromUrl(unitIds, rows),
  );

  // Re-initialize when rows reference changes (data reload / new dataset).
  // unitIds is intentionally read from the render closure (not in deps) so URL
  // changes triggered by the user's own selection do NOT cause a re-init loop.
  // Using "derive state during render" pattern instead of useEffect to avoid cascading renders.
  const [prevRows, setPrevRows] = useState(rows);
  if (prevRows !== rows) {
    setPrevRows(rows);
    setSelectionModel(buildModelFromUrl(unitIds, rows));
  }

  const handleSelectionChange = (model) => {
    // Always persist the full model so subsequent renders pass it back unchanged.
    setSelectionModel(model);

    if (props.onSelectionChange) {
      props.onSelectionChange(model);
      return;
    }

    // Normalise to selected leaf IDs regardless of model type.
    // - include model: model.ids = selected rows (may include group IDs)
    // - exclude model: model.ids = excluded rows; everything else is selected
    let selectedLeafIds;
    if (model.type === 'exclude') {
      selectedLeafIds = rows
        .map((r) => r.id)
        .filter((id) => !model.ids.has(id));
    } else {
      selectedLeafIds = Array.from(model.ids).filter((id) => rowIds.has(id));
    }

    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (selectedLeafIds.length === rows.length) {
        next.delete('units');
      } else if (selectedLeafIds.length === 0) {
        next.set('units', 'none');
      } else {
        next.set('units', selectedLeafIds.map(String).join(','));
      }
      return next;
    });
  };

  const baseSx = {
    bgcolor: 'transparent',
    border: 'none',
    '& .MuiDataGrid-row.Mui-selected': {
      bgcolor: 'transparent',
      '&:hover': {
        bgcolor: 'action.hover',
      },
    },
  };

  return (
    <Box sx={{ width: '100%', height: '410px' }}>
      <DataGridPremium
        {...props}
        rowSelectionPropagation={{ parents: true, descendants: true }}
        onRowSelectionModelChange={handleSelectionChange}
        checkboxSelection
        rows={rows}
        getRowId={(row) => row.id}
        rowsPerPageOptions={[5]}
        rowSelectionModel={selectionModel}
        initialState={{
          ...props.initialState,
          sorting: {
            sortModel: [{ field: 'sum', sort: 'desc' }],
          },
        }}
        disableColumnMenu={disableColumnMenu}
        showToolbar
        loading={isLoading}
        slots={{ toolbar: DataGridToolbar }}
        slotProps={{
          baseCheckbox: {
            color: 'info',
          },
          loadingOverlay: {
            variant: 'linear-progress',
            noRowsVariant: 'skeleton',
          },
          toolbar: {
            showQuickFilter: true,
            title: label,
          },
        }}
        hideFooter
        sx={baseSx}
      />
    </Box>
  );
};

export default DashboardDatagrid;
